import type {
  BrandSpecials as SDKBrandSpecials,
  Specials,
} from '@iheartjane/dm-sdk';
import { Placement, SmartSort } from '@iheartjane/dm-sdk';
import { FLAGS } from '@iheartjane/feature-flags';
import type { Nullable } from '@iheartjane/types';
import kebabCase from 'lodash-es/kebabCase';
import { useMemo } from 'react';

import { DMMenuRow } from '@jane/bloom/components/menuRow/dmMenuRow';
import { JaneGoldRow } from '@jane/bloom/components/menuRow/janeGoldRow';
import { MenuRow } from '@jane/bloom/components/menuRow/menuRow';
import { SpecialsRow } from '@jane/bloom/components/menuRow/specialsRow';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useSkeletonLoader } from '@jane/bloom/hooks/useSkeletonLoader';
import type { Cart } from '@jane/bloom/types/schemas/bloom/checkout';
import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { BrandSpecials } from '@jane/bloom/types/schemas/marketplace/brandSpecials';
import { isJaneUser } from '@jane/bloom/types/schemas/marketplace/janeUser';
import type { StoreSpecial } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import type { ComplianceLanguage } from '@jane/bloom/types/schemas/roots/complianceLanguage';
import { isEmpty } from '@jane/bloom/utils/guards/isEmpty';
import { sortSpecials } from '@jane/bloom/utils/specials/sortSpecials';
import type { JaneGold } from '@jane/bloom/viewModels/productDetail';
import type { StoreViewModel } from '@jane/bloom/viewModels/store';
import type { AlgoliaProduct } from '@jane/search/types';

export type FeaturedTabProps = {
  brandSpecials: BrandSpecials;
  cart?: Nullable<Cart>;
  complianceLanguage?: Nullable<ComplianceLanguage>;
  customLanguage: CustomLanguage;
  enabledSpecialsForRow: StoreSpecial[];
  featuredPlacements: (string | Specials | SDKBrandSpecials)[];
  janeGold: JaneGold;
  menuTopRowInstance?: SmartSort<AlgoliaProduct>;
  pauseRefetching: () => void;
  pinnedStore?: Nullable<StoreViewModel>;
  unpauseRefetching: () => void;
  userHasLinkedBank?: boolean;
};

export function FeaturedTab({
  brandSpecials,
  cart,
  complianceLanguage,
  customLanguage,
  enabledSpecialsForRow,
  featuredPlacements,
  janeGold,
  menuTopRowInstance,
  pauseRefetching,
  pinnedStore,
  unpauseRefetching,
  userHasLinkedBank,
}: FeaturedTabProps) {
  const { featureFlags, whoami } = useRootLoaderData();
  const authenticated = isJaneUser(whoami.user);
  const { skeletonLoader } = useSkeletonLoader();
  const storeSpecials = useMemo(() => {
    if (!isEmpty(enabledSpecialsForRow)) {
      return sortSpecials([...enabledSpecialsForRow]);
    }
    return null;
  }, [enabledSpecialsForRow]);

  if (skeletonLoader) {
    return skeletonLoader;
  }

  const specialsRedesignMenu = featureFlags[FLAGS.c3SpecialsRedesignMenu];

  return (
    <>
      {menuTopRowInstance && (
        <DMMenuRow
          pauseRefetching={pauseRefetching}
          unpauseRefetching={unpauseRefetching}
          brandSpecials={brandSpecials}
          menuTopRow={menuTopRowInstance}
          cart={cart}
          customLanguage={customLanguage}
          specials={storeSpecials}
          janeGold={janeGold}
          pinnedStore={pinnedStore}
        />
      )}

      {featuredPlacements.map((placement, index) => {
        if (
          typeof placement !== 'string' &&
          placement.placement === Placement.Specials &&
          storeSpecials
        ) {
          return (
            <SpecialsRow
              className="customRow"
              complianceLanguage={complianceLanguage}
              displayName={placement.title}
              key={`specials-placement-${index}`}
              reservationModeLabels={customLanguage.reservationModeLabels}
              specialsRedesignMenu={specialsRedesignMenu}
              storeSpecials={storeSpecials}
              {...(!specialsRedesignMenu && {
                mode: 'ideal-width',
              })}
            />
          );
        } else if (
          typeof placement !== 'string' &&
          placement.placement === Placement.BrandSpecials &&
          janeGold.shouldShowGold
        ) {
          return (
            <JaneGoldRow
              authenticated={authenticated}
              brandSpecials={brandSpecials}
              className="janeGoldRow"
              isFeaturedTab={true}
              key={`brand-specials-placement-${index}`}
              state={pinnedStore?.state ?? ''}
              title={placement.title}
              userHasLinkedBank={userHasLinkedBank}
            />
          );
        } else if (typeof placement === 'string') {
          const instance = SmartSort.fromJSON<AlgoliaProduct>(placement);
          return (
            <MenuRow
              brandSpecials={brandSpecials}
              cart={cart}
              customLanguage={customLanguage}
              customRowId={instance.rowId ?? 0}
              customRowSlug={kebabCase(instance.title)}
              key={`menu-row-placement-${index}`}
              displayName={instance.title ?? null}
              specials={storeSpecials}
              stringifiedInstance={instance.toJSON()}
              janeGold={janeGold}
              pinnedStore={pinnedStore}
              placement={instance.placement}
            />
          );
        }

        return null;
      })}
    </>
  );
}
